import { GiftIcon, ImageIcon } from "@xola/icons";
import { Breadcrumb, HeaderToolbar } from "@xola/ui-kit";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { GiftOffer, PurchaseRule } from "types";
import { Page } from "../Page";
import { api } from "@/api/api";
import { queryClient } from "@/api/queryClient";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { SidebarTabs } from "@/components/Common/SidebarTabs";
import { ColumnLayout } from "@/components/Layout/ColumnLayout";
import { Loader } from "@/components/Loader";
import { EditGiftOffer } from "@/components/Product/EditGiftOffer";
import { EditGiftOfferRestrictions } from "@/components/Product/EditGiftOfferRestrictions";
import { GiftOfferPhotoList } from "@/components/Product/GiftOfferPhotoList";
import { useExperiences } from "@/hooks/resources/useProduct";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useResource } from "@/hooks/useResource";

export const GiftOfferPage = () => {
    const history = useHistory();
    const invalidate = useInvalidate();

    const { id, section } = useParams<{ id?: ID; section?: string }>();

    const { data: giftOffer } = useResource<GiftOffer>(["giftOffers", id]);

    const { data: deletedExperiences } = useExperiences({ deleted: true });
    const deletedExperiencesIdList = useMemo(() => {
        return deletedExperiences.map((experience) => experience.id);
    }, [deletedExperiences]);

    const purchaseRuleCache = queryClient.getQueryData<PurchaseRule>(["purchaseRules", giftOffer?.purchaseRule.id]);

    // API doesn't return it with expand param at the moment
    const { data: purchaseRule, isLoading: isLoadingPurchaseRule } = useResource<PurchaseRule>([
        "purchaseRules",
        giftOffer?.purchaseRule?.id,
    ]);

    const { data: redemptionPurchaseRule, isLoading: isLoadingRedemptionPurchaseRule } = useResource<PurchaseRule>([
        "purchaseRules",
        giftOffer?.redemptionPurchaseRule?.id,
    ]);

    const { mutateAsync: saveRestrictions } = useMutation(async (giftOffer: GiftOffer) => {
        await api.put(`/giftOffers/${giftOffer.id}`, giftOffer);
        await api.put(`/purchaseRules/${giftOffer?.purchaseRule.id}`, giftOffer.purchaseRule);
        if (giftOffer.redemptionPurchaseRule.filter) {
            await api.put(`/purchaseRules/${giftOffer?.redemptionPurchaseRule.id}`, giftOffer.redemptionPurchaseRule);
        }

        invalidate("giftOffers");
        invalidate("purchaseRules", giftOffer?.purchaseRule.id);
        invalidate("purchaseRules", giftOffer?.redemptionPurchaseRule.id);
    });

    const handleCancel = () => {
        history.push("/products/giftOffers");
    };

    const initialSection = section ?? tabTitles[0].section;
    const initialActiveIndex = getSettingsIndex(initialSection);
    const [activeTabIndex, setActiveTabIndex] = useState(initialActiveIndex === -1 ? 0 : initialActiveIndex);

    // To Check if the gift offer is new or not
    const [isNewGift] = useState(!purchaseRuleCache);

    const breadcrumbs: Array<{ label: ReactNode; url?: string; tabIndex?: number }> = [
        { label: "Products", url: "/products/giftOffers" },
        { label: "Gifts", url: "/products/giftOffers" },
        { label: giftOffer?.name ? <NoTranslate>{giftOffer.name}</NoTranslate> : "New", tabIndex: 0 },
        { label: tabTitles[activeTabIndex].label, tabIndex: activeTabIndex },
    ];

    useEffect(() => {
        if (section) {
            const newIndex = tabTitles.findIndex((tab) => tab.section === section);
            if (newIndex >= 0 && activeTabIndex !== newIndex) {
                setActiveTabIndex(newIndex);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section]);

    const handleBreadcrumbClick = (index: number, url?: string) => {
        if (url) {
            history.push(url);
        } else {
            handleActiveTabChange(index);
        }
    };

    const handleActiveTabChange = (index: number) => {
        if (id) {
            const path = `/products/giftOffers/${id}/${tabTitles[index].section}`;
            history.push(path);
        } else {
            setActiveTabIndex(index);
        }
    };

    return (
        <Page isDynamicTitle={!!giftOffer} title={giftOffer ? giftOffer.name : "Create Gift"}>
            <ColumnLayout>
                <HeaderToolbar>
                    <HeaderToolbar.Breadcrumb>
                        {breadcrumbs.map((breadcrumb) => (
                            <Breadcrumb.Item
                                key={breadcrumb.label}
                                onClick={() => handleBreadcrumbClick(breadcrumb.tabIndex ?? 0, breadcrumb.url)}
                            >
                                {breadcrumb.label}
                            </Breadcrumb.Item>
                        ))}
                    </HeaderToolbar.Breadcrumb>
                </HeaderToolbar>

                <ColumnLayout.Fill>
                    <SidebarTabs activeTab={activeTabIndex} onClick={handleActiveTabChange}>
                        <SidebarTabs.Tab
                            name={tabTitles[0].label}
                            description="Configure gift certificates at a fixed or variable price that can be applied across some or all of your experiences."
                            icon={<GiftIcon />}
                        >
                            <EditGiftOffer giftOffer={giftOffer} purchaseRule={purchaseRule} />
                        </SidebarTabs.Tab>

                        <SidebarTabs.Tab
                            name={tabTitles[1].label}
                            description="Set restrictions around when and how your gift certificates will be used."
                            icon={<GiftIcon />}
                            isHidden={!giftOffer}
                        >
                            {isLoadingPurchaseRule || isLoadingRedemptionPurchaseRule ? (
                                <Loader />
                            ) : giftOffer && purchaseRule && redemptionPurchaseRule ? (
                                <EditGiftOfferRestrictions
                                    giftOffer={giftOffer}
                                    deletedExperiencesIdList={deletedExperiencesIdList}
                                    purchaseRule={purchaseRule}
                                    redemptionPurchaseRule={redemptionPurchaseRule}
                                    isNewGift={isNewGift}
                                    onSubmit={saveRestrictions}
                                    onCancel={handleCancel}
                                />
                            ) : (
                                "No Purchase Rule or Redemption Purchase Rule found"
                            )}
                        </SidebarTabs.Tab>

                        <SidebarTabs.Tab
                            name={tabTitles[2].label}
                            description="Set as your Primary Photo will be set as the image in the back office and in online checkout."
                            icon={<ImageIcon />}
                            isHidden={!giftOffer}
                        >
                            {giftOffer && <GiftOfferPhotoList giftOffer={giftOffer} />}
                        </SidebarTabs.Tab>
                    </SidebarTabs>
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};

const tabTitles = [
    { section: "description", label: "Description & Terms" },
    { section: "restrictions", label: "Restrictions" },
    { section: "photos", label: "Photos" },
];

const getSettingsIndex = (section = tabTitles[0].section) => {
    return Math.max(
        0,
        tabTitles.findIndex((tab) => tab.section === section),
    );
};
